import React from "react";

/**
 * Resilient lazy component loader.
 * Automatically retries dynamic module imports if a temporary network blip
 * or Vite dev-server restart occurs.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 3,
  delayMs = 800
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    new Promise<{ default: T }>((resolve, reject) => {
      const attempt = (remainingAttempts: number) => {
        factory()
          .then(resolve)
          .catch((error) => {
            if (remainingAttempts <= 1) {
              const msg = String(error?.message || error || "");
              const isChunkLoadFailure =
                msg.includes("Failed to fetch dynamically imported module") ||
                msg.includes("Importing a module script failed") ||
                msg.includes("loading chunk");

              if (isChunkLoadFailure && typeof window !== "undefined") {
                const reloadKey = "catchai_last_chunk_autoreload";
                const lastReload = sessionStorage.getItem(reloadKey);
                const now = Date.now();
                if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
                  sessionStorage.setItem(reloadKey, now.toString());
                  window.location.reload();
                  return;
                }
              }
              reject(error);
              return;
            }
            setTimeout(() => attempt(remainingAttempts - 1), delayMs);
          });
      };
      attempt(retries);
    })
  );
}
